import OpenAI from 'openai'

interface TTSConfig {
  useOpenAI?: boolean
  apiKey?: string
  voice?: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer'
  model?: 'tts-1' | 'tts-1-hd'
}

class TTSService {
  private openaiClient: OpenAI | null = null
  private audioContext: AudioContext | null = null
  private config: TTSConfig = {}

  constructor(config: TTSConfig = {}) {
    this.config = {
      useOpenAI: config.useOpenAI ?? false,
      voice: config.voice ?? 'nova',
      model: config.model ?? 'tts-1',
      ...config
    }

    if (this.config.useOpenAI && this.config.apiKey) {
      this.openaiClient = new OpenAI({
        apiKey: this.config.apiKey,
        dangerouslyAllowBrowser: true
      })
    }
  }

  async speak(text: string, onStart?: () => void, onEnd?: () => void): Promise<void> {
    // Stop any existing speech FIRST
    this.stop()
    
    // Only wait if there's actual speech playing
    if (speechSynthesis.speaking) {
      await new Promise(resolve => setTimeout(resolve, 40))
    }
    
    if (this.config.useOpenAI && this.openaiClient) {
      try {
        await this.speakWithOpenAI(text, onStart, onEnd)
        return
      } catch (error) {
        console.error('OpenAI TTS failed, falling back to Web Speech API:', error)
        // Fallback to Web Speech API
      }
    }

    this.speakWithWebSpeech(text, onStart, onEnd)
  }

  private async speakWithOpenAI(
    text: string,
    onStart?: () => void,
    onEnd?: () => void
  ): Promise<void> {
    if (!this.openaiClient) {
      throw new Error('OpenAI client not initialized')
    }

    const audio = await this.openaiClient.audio.speech.create({
      model: this.config.model!,
      voice: this.config.voice!,
      input: text
    })

    const arrayBuffer = await audio.arrayBuffer()
    const audioBuffer = await this.decodeAudioData(arrayBuffer)

    await this.playAudioBuffer(audioBuffer, onStart, onEnd)
  }

  private speakWithWebSpeech(
    text: string,
    onStart?: () => void,
    onEnd?: () => void
  ): void {
    if (!('speechSynthesis' in window)) {
      onEnd?.()
      return
    }

    if (speechSynthesis.speaking) {
      speechSynthesis.cancel()
    }

    // Start immediately, no delay
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.rate = 0.85
    utterance.pitch = 1.1
    utterance.volume = 0.9
    utterance.lang = 'en-US'

    const voices = speechSynthesis.getVoices()

    const voicePriorities = [
      'Victoria',
      'Samantha',
      'Karen',
      'Female',
      'en-US',
      'en-GB'
    ]

    let selectedVoice = null
    for (const priority of voicePriorities) {
      selectedVoice = voices.find(
        voice => voice.name.includes(priority) && voice.lang.startsWith('en')
      )
      if (selectedVoice) {
        break
      }
    }

    if (selectedVoice) {
      utterance.voice = selectedVoice
    }

    utterance.onstart = () => {
      onStart?.()
    }

    utterance.onend = () => {
      onEnd?.()
    }

    utterance.onerror = event => {
      console.error('Speech synthesis error:', event)
      onEnd?.()
    }

    speechSynthesis.speak(utterance)
  }

  private async decodeAudioData(arrayBuffer: ArrayBuffer): Promise<AudioBuffer> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext()
    }

    return await this.audioContext.decodeAudioData(arrayBuffer)
  }

  private async playAudioBuffer(
    audioBuffer: AudioBuffer,
    onStart?: () => void,
    onEnd?: () => void
  ): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext()
    }
    
    // Resume audio context if suspended (required for autoplay policy)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume()
    }

    const source = this.audioContext.createBufferSource()
    source.buffer = audioBuffer
    source.connect(this.audioContext.destination)

    source.onended = () => {
      onEnd?.()
    }

    // Call onStart right before playing the audio
    onStart?.()
    source.start()
  }

  stop(): void {
    // Stop all speech synthesis
    if (speechSynthesis.speaking) {
      speechSynthesis.cancel()
      speechSynthesis.cancel() // Call twice to be sure
    }
    
    // Stop and close audio context
    if (this.audioContext) {
      if (this.audioContext.state !== 'closed') {
        this.audioContext.close()
      }
      this.audioContext = null
    }
  }
}

export default TTSService
